/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Vault service. Built around the F6 envelope (`./vault/envelope.ts`) and
 * F7 rollback tracker (`./vault/rollback.ts`). Replaces the previous
 * KDBX4-based implementation entirely — no migration path (greenfield,
 * pre-launch).
 *
 * Responsibilities:
 *   - Persist a single VaultContents envelope to the keystore endpoint.
 *   - Hold the unlocked VaultContents in memory between unlock and lock.
 *   - Manage identities (ML-KEM-768 + ML-DSA-65 keypairs) inside the vault.
 *
 * What this file does NOT do:
 *   - Lifecycle state transitions (active → retiring → retired) —
 *     F11 / #51 layers the state machine on top of the F6 schema.
 *
 * What this file DOES do that may surprise readers:
 *   - signContent / retrievePrivateKey — F5c / #65 lands here. The signer
 *     hides the active-identity lookup so callers never juggle raw key
 *     bytes themselves; retrievePrivateKey is the lower-level escape
 *     hatch for routing-sheet builders that need to assemble payloads
 *     keyed by a specific identity.
 *   - storeSymmetricKey / retrieveSymmetricKey / listSymmetricKeys —
 *     #247 lands the DK (document-key) lifecycle inside
 *     `VaultContents.symmetricKeys`; persistence rides the same envelope
 *     save path (rollback counter + If-Match untouched).
 *   - Local envelope backup — every successful save mirrors the
 *     *encrypted* envelope into localStorage (best-effort) so a lost
 *     server blob is recoverable from the device that wrote it.
 */

import { base64nopad } from '@scure/base';
import { sha256 } from '@noble/hashes/sha2.js';

import { tdFetch } from './tdfetch.js';
import { defaultRandom, type Random } from './random.js';

import {
  decrypt as envelopeDecrypt,
  encrypt as envelopeEncrypt,
  EnvelopeOpenError,
} from './vault/envelope.js';
import {
  localStorageTracker,
  recordSave,
  RollbackDetectedError,
  type RollbackTracker,
} from './vault/rollback.js';
import type {
  EnvelopeOuterSerialized,
  Identity,
  StoredSymmetricKey,
  VaultContents,
} from './vault/schema.js';
import {
  SYMMETRIC_KEY_BYTES,
  VAULT_CONTENTS_SCHEMA_VERSION,
} from './vault/schema.js';
import { registerUnsavedDataCheck } from './unsaved.js';

import { keyGen as mlkemKeyGen } from './crypto/mlkem.js';
import { keyGen as mldsaKeyGen, sign as mldsaSign } from './crypto/mldsa.js';

const KEYSTORE_ENDPOINT = '/api/key/member/keystore';
const ENVELOPE_ID_BYTES = 16;

/**
 * localStorage key prefix for the local encrypted-envelope backup
 * (#247). Versioned like `rollback.ts`'s counter prefix so a future
 * format change can sweep old entries by prefix.
 */
export const BACKUP_STORAGE_KEY_PREFIX = 'td.vault.backup.v1.';

interface KeystoreResponse {
  checksum: string;
  sequence: number;
  body: string;
}

const nowIso = (): string => new Date().toISOString();

const randomEnvelopeId = (rng: Random): string =>
  base64nopad.encode(rng.getRandomBytes(ENVELOPE_ID_BYTES));

/**
 * Frozen-empty `VaultContents` for a brand-new vault. Identities are
 * appended via `createIdentity` after construction.
 */
const emptyContents = (rng: Random): VaultContents => ({
  schemaVersion: VAULT_CONTENTS_SCHEMA_VERSION,
  envelopeId: randomEnvelopeId(rng),
  createdAt: nowIso(),
  updatedAt: nowIso(),
  identities: [],
  contactKeys: [],
  symmetricKeys: [],
  auditLog: [
    {
      at: nowIso(),
      kind: 'created',
      details: {},
    },
  ],
});

/**
 * Service that manages the user's encrypted vault. Singleton-friendly
 * but constructable directly for tests with an injected RNG / tracker.
 */
export class VaultService {
  /** Last-fetched serialized envelope from the server (or null if unloaded). */
  private fetchedEnvelope: EnvelopeOuterSerialized | null = null;
  /** Server-reported sequence number for optimistic concurrency / If-Match. */
  private fetchedSequence = 0;
  /** Decrypted contents, present iff the vault is unlocked. */
  private contents: VaultContents | null = null;
  /** Passphrase held in memory while unlocked, needed for save. Cleared on lock. */
  private cachedPassphrase: string | null = null;
  /**
   * True when in-memory contents diverge from the last-saved envelope
   * (#247). Set by every mutator, cleared on save / lock / unlock.
   * Consulted by `auth.logout()` via the `unsaved.ts` registry.
   */
  private dirty = false;

  constructor(
    private readonly rng: Random = defaultRandom,
    private readonly tracker: RollbackTracker = lazyLocalStorageTracker()
  ) {}

  /**
   * Fetch the envelope from the server. Returns true if a vault exists
   * for this user (and the bytes survived a content-checksum check).
   */
  async checkVault(): Promise<boolean> {
    let response: Response;
    try {
      response = await tdFetch(KEYSTORE_ENDPOINT);
    } catch {
      return false;
    }
    if (!response.ok) {
      return false;
    }
    const data = (await response.json()) as KeystoreResponse;
    if (!verifyKeystoreChecksum(data)) {
      return false;
    }
    const envelopeJsonBytes = base64nopad.decode(data.body);
    let envelopeJson: string;
    try {
      envelopeJson = new TextDecoder('utf-8', { fatal: true }).decode(
        envelopeJsonBytes
      );
    } catch {
      return false;
    }
    let parsed: EnvelopeOuterSerialized;
    try {
      parsed = JSON.parse(envelopeJson) as EnvelopeOuterSerialized;
    } catch {
      return false;
    }
    this.fetchedEnvelope = parsed;
    this.fetchedSequence = data.sequence;
    return true;
  }

  /** Whether a server envelope has been loaded (regardless of unlock state). */
  hasVault(): boolean {
    return this.fetchedEnvelope !== null;
  }

  /** Whether the vault is currently unlocked (decrypted contents in memory). */
  isUnlocked(): boolean {
    return this.contents !== null;
  }

  /**
   * Compatibility shim used by `TdVaultLock` and `TdStoryAction`: the
   * legacy API returned `true` only when the kdbx db was loaded *and*
   * the vault file was present. Maps to `isUnlocked()` in the new model.
   */
  async isVaultReady(): Promise<boolean> {
    return this.isUnlocked();
  }

  /**
   * Create a brand-new vault: generate a fresh envelopeId, an initial
   * Identity, encrypt with the supplied passphrase, post to the server.
   *
   * @throws Error if the server rejects the create request.
   */
  async createVault(passphrase: string): Promise<void> {
    const contents = emptyContents(this.rng);
    const identity = await createIdentityFor(contents, this.rng);
    contents.identities.push(identity);
    contents.updatedAt = nowIso();

    const counter = 0;
    const envelope = await envelopeEncrypt({
      passphrase,
      contents,
      rng: this.rng,
      envelopeId: contents.envelopeId,
      counter,
    });

    await postEnvelope(envelope, /* sequence */ 1, /* ifMatch */ null);

    recordSave(envelope.envelopeId, counter, this.tracker);
    backupEnvelopeLocally(envelope, 1);

    this.fetchedEnvelope = envelope;
    this.fetchedSequence = 1;
    this.contents = contents;
    this.cachedPassphrase = passphrase;
    this.dirty = false;
    dispatchVaultUnlocked();
  }

  /**
   * Decrypt the previously-fetched envelope with the supplied passphrase.
   * Performs rollback verification via the tracker after AEAD success.
   *
   * @throws {EnvelopeOpenError} on wrong passphrase / tampered envelope
   * @throws {RollbackDetectedError} when the envelope counter is older
   *   than the tracker's recorded high-water mark
   * @throws Error('No vault loaded') when checkVault hasn't run successfully
   */
  async unlockVault(passphrase: string): Promise<void> {
    if (this.fetchedEnvelope === null) {
      throw new Error('No vault loaded — call checkVault() first');
    }
    const contents = await envelopeDecrypt({
      passphrase,
      envelope: this.fetchedEnvelope,
      tracker: this.tracker,
    });
    this.contents = contents;
    this.cachedPassphrase = passphrase;
    this.dirty = false;
    dispatchVaultUnlocked();
  }

  /**
   * Re-encrypt the in-memory contents with a bumped counter and PUT to
   * the server with `If-Match` carrying the previous sequence (per
   * backend issue B1 / app-backend#36). Updates the rollback tracker
   * post-confirm.
   *
   * @throws Error if the vault is locked or the server returns non-OK.
   */
  async saveVault(): Promise<void> {
    if (this.contents === null || this.cachedPassphrase === null) {
      throw new Error('Vault is locked');
    }
    const previousCounter = this.fetchedEnvelope?.counter ?? 0;
    const newCounter = previousCounter + 1;

    const updated: VaultContents = {
      ...this.contents,
      updatedAt: nowIso(),
    };
    const envelope = await envelopeEncrypt({
      passphrase: this.cachedPassphrase,
      contents: updated,
      rng: this.rng,
      envelopeId: this.contents.envelopeId,
      counter: newCounter,
    });

    const newSequence = this.fetchedSequence + 1;
    await postEnvelope(envelope, newSequence, this.fetchedSequence);

    recordSave(envelope.envelopeId, newCounter, this.tracker);
    backupEnvelopeLocally(envelope, newSequence);

    this.fetchedEnvelope = envelope;
    this.fetchedSequence = newSequence;
    this.contents = updated;
    this.dirty = false;
  }

  /**
   * Drop decrypted state from memory. The fetched envelope stays loaded
   * so the user can `unlockVault` again without a server round-trip.
   * Best-effort cached-passphrase wipe (JS strings can't be zeroed; the
   * reference release is what we control).
   */
  lock(): void {
    this.contents = null;
    this.cachedPassphrase = null;
    this.dirty = false;
    dispatchVaultLocked();
  }

  /**
   * True when in-memory contents hold changes that have not been saved
   * to the server yet. `auth.logout()` consults this (via `unsaved.ts`)
   * to warn before discarding state. Always false while locked.
   */
  hasUnsavedChanges(): boolean {
    return this.dirty;
  }

  /** Append a fresh active identity. Vault must be unlocked. */
  async createIdentity(): Promise<Identity> {
    if (this.contents === null) {
      throw new Error('Vault is locked');
    }
    const identity = await createIdentityFor(this.contents, this.rng);
    this.contents.identities.push(identity);
    this.dirty = true;
    return identity;
  }

  /**
   * Read-only snapshot of the in-memory `VaultContents`. Returns `null`
   * when the vault is locked. Callers that want to *update* contents
   * (e.g. F12 TOFU pinning in `storyprocessor.js`) must use
   * `updateContents` so the next `save()` picks up the new bytes.
   */
  currentContents(): VaultContents | null {
    return this.contents;
  }

  /**
   * Replace the in-memory contents wholesale. Used by flows that build
   * a new immutable `VaultContents` (e.g. `pinIfNew`) and need the
   * change to live until the next explicit `save()` pushes it to the
   * server. Throws when the vault is locked — there's no path that
   * should write into a locked vault.
   */
  updateContents(next: VaultContents): void {
    if (this.contents === null) {
      throw new Error('Vault is locked');
    }
    this.contents = next;
    this.dirty = true;
  }

  /** All identities currently in the vault. Empty array if locked. */
  listIdentities(): readonly Identity[] {
    return this.contents?.identities ?? [];
  }

  /** Lookup by id; null if not found or vault locked. */
  getIdentity(id: string): Identity | null {
    return this.contents?.identities.find(i => i.id === id) ?? null;
  }

  /** First identity in `'active'` state, or null. */
  getActiveIdentity(): Identity | null {
    return this.contents?.identities.find(i => i.state === 'active') ?? null;
  }

  /**
   * Retrieve the raw private-key bytes for one half of an identity's
   * keypair. Used by routing-sheet building (F14) and by the local sign
   * path. Returns `null` when no identity matches `identityId`. Throws
   * when the vault is locked — there's no path that should call this
   * before unlock.
   *
   * Returns a fresh `slice()` copy of the vault's internal private-key
   * bytes (#116). Without the copy, a caller who follows the
   * "ephemeral" guidance below and zeroes / mutates the returned
   * buffer would silently corrupt the vault's in-memory state,
   * breaking every later sign / unwrap with a cryptic noble error and
   * no path to recovery. The allocation cost is negligible (private
   * keys are ≤ 4032 bytes; allocations are infrequent compared with
   * the ML-KEM / ML-DSA op that follows).
   *
   * Treat the returned bytes as ephemeral: callers should not stash
   * them in long-lived state or pass them across module boundaries
   * beyond the single operation that needed them. Zeroing the buffer
   * after use is safe — the vault keeps its own copy.
   */
  retrievePrivateKey(
    identityId: string,
    algorithm: 'kem' | 'sig'
  ): Uint8Array | null {
    if (this.contents === null) {
      throw new Error('Vault is locked');
    }
    const identity = this.contents.identities.find(i => i.id === identityId);
    if (identity === undefined) return null;
    return algorithm === 'kem'
      ? identity.kem.privateKey.slice()
      : identity.sig.privateKey.slice();
  }

  /**
   * Sign `content` with the active identity's ML-DSA-65 signing key.
   *
   * The active-identity lookup is intentionally inside this call — UI
   * code shouldn't need to know which identity is current. If the
   * caller wants to sign with a *specific* identity, they should reach
   * for `retrievePrivateKey` + `mldsaSign` directly.
   *
   * @throws Error if the vault is locked or no identity is `active`.
   */
  signContent(content: Uint8Array): SignedContent {
    if (this.contents === null) {
      throw new Error('Vault is locked');
    }
    const identity = this.getActiveIdentity();
    if (identity === null) {
      throw new Error('No active identity in vault');
    }
    const signature = mldsaSign(identity.sig.privateKey, content);
    return {
      signature,
      signingKeyId: identity.id,
      algorithm: 'ML-DSA-65',
    };
  }

  /**
   * Store a document key (DK) in the vault (#247). `id` is the F13
   * documentId (UUID). The bytes are copied on the way in, so the caller
   * may zero its own buffer afterwards.
   *
   * Idempotent for pipeline resume: re-storing the same `id` with the
   * same bytes is a no-op (the tickler can replay a failed
   * `saveSymmetricKey` step safely). The same `id` with *different*
   * bytes throws — that is a documentId collision and always a bug.
   *
   * In-memory only until the next `saveVault()`; callers that need
   * durability (storyprocessor step 2) must save afterwards.
   *
   * @throws Error when the vault is locked, the key is not 32 bytes, or
   *   `id` already exists with different bytes.
   */
  storeSymmetricKey(params: { id: string; key: Uint8Array }): void {
    if (this.contents === null) {
      throw new Error('Vault is locked');
    }
    if (params.id.trim() === '') {
      throw new Error('symmetric key id must be non-empty');
    }
    if (params.key.length !== SYMMETRIC_KEY_BYTES) {
      throw new Error(
        `symmetric key must be ${SYMMETRIC_KEY_BYTES} bytes, got ${params.key.length}`
      );
    }
    const existing = this.contents.symmetricKeys.find(k => k.id === params.id);
    if (existing !== undefined) {
      if (bytesEqual(existing.key, params.key)) {
        return; // resume replay — already stored
      }
      throw new Error(
        `symmetric key '${params.id}' already exists with different bytes`
      );
    }
    this.contents.symmetricKeys.push({
      id: params.id,
      algorithm: 'xchacha20-poly1305',
      key: params.key.slice(),
      createdAt: nowIso(),
    });
    this.contents.auditLog.push({
      at: nowIso(),
      kind: 'created',
      details: { symmetricKeyId: params.id },
    });
    this.dirty = true;
  }

  /**
   * Retrieve the DK bytes stored under `id`, or null when unknown.
   * Returns a fresh `slice()` copy for the same reason as
   * `retrievePrivateKey` (#116): a caller zeroing the returned buffer
   * must not corrupt the vault's in-memory state.
   *
   * @throws Error when the vault is locked.
   */
  retrieveSymmetricKey(id: string): Uint8Array | null {
    if (this.contents === null) {
      throw new Error('Vault is locked');
    }
    const record = this.contents.symmetricKeys.find(k => k.id === id);
    return record?.key.slice() ?? null;
  }

  /**
   * Metadata (no key bytes) for every stored DK. Empty array while
   * locked, mirroring `listIdentities`. Callers that need the bytes go
   * through `retrieveSymmetricKey` one id at a time.
   */
  listSymmetricKeys(): ReadonlyArray<
    Pick<StoredSymmetricKey, 'id' | 'algorithm' | 'createdAt'>
  > {
    return (this.contents?.symmetricKeys ?? []).map(
      ({ id, algorithm, createdAt }) => ({ id, algorithm, createdAt })
    );
  }
}

/**
 * Result of `VaultService.signContent`. The `signingKeyId` is the
 * identity ID — recipients use it to look up the matching public
 * verification key in their pinned-contacts store (F12) or in the
 * signed key bundle from the server (F12 + F12-wiring / #83).
 */
export interface SignedContent {
  signature: Uint8Array;
  signingKeyId: string;
  algorithm: 'ML-DSA-65';
}

/**
 * Build a fresh active identity. Each identity holds an ML-KEM-768
 * encryption keypair and an ML-DSA-65 signing keypair; both are
 * generated through the F3 RNG seam so tests can inject `seededRandom`.
 */
const createIdentityFor = async (
  contents: VaultContents,
  rng: Random
): Promise<Identity> => {
  const kemPair = mlkemKeyGen(rng);
  const sigPair = mldsaKeyGen(rng);
  // Identity ID is a hash of the KEM public key — deterministic and
  // small enough to use as a key in lookup tables.
  const id = base64nopad.encode(sha256(kemPair.publicKey)).slice(0, 22);
  contents.auditLog.push({
    at: nowIso(),
    kind: 'created',
    details: { identityId: id },
  });
  return {
    id,
    state: 'active',
    createdAt: nowIso(),
    kem: {
      algorithm: 'ML-KEM-768',
      publicKey: kemPair.publicKey,
      privateKey: kemPair.privateKey,
    },
    sig: {
      algorithm: 'ML-DSA-65',
      publicKey: sigPair.publicKey,
      privateKey: sigPair.privateKey,
    },
  };
};

/** Plain (non-constant-time) byte comparison — both sides are local secrets. */
const bytesEqual = (a: Uint8Array, b: Uint8Array): boolean =>
  a.length === b.length && a.every((byte, i) => byte === b[i]);

/**
 * Shape of one local envelope backup entry (#247): the encrypted
 * envelope exactly as sent to the server, plus the server sequence it
 * was stored under.
 */
export interface LocalVaultBackup {
  sequence: number;
  envelope: EnvelopeOuterSerialized;
}

/**
 * Mirror the just-saved *encrypted* envelope into localStorage (#247).
 * Best-effort: quota exhaustion or unavailable storage logs a warning
 * and never fails the save — the server copy is canonical, the local
 * copy is a recovery aid. The blob is passphrase-encrypted, so
 * localStorage exposure adds nothing beyond what the server already
 * holds.
 */
const backupEnvelopeLocally = (
  envelope: EnvelopeOuterSerialized,
  sequence: number
): void => {
  try {
    const backup: LocalVaultBackup = { sequence, envelope };
    localStorage.setItem(
      `${BACKUP_STORAGE_KEY_PREFIX}${envelope.envelopeId}`,
      JSON.stringify(backup)
    );
  } catch (error) {
    console.warn('vault: local envelope backup skipped:', error);
  }
};

/**
 * Read back the local envelope backup for `envelopeId`, or null when
 * absent / unreadable. Consumer: the future recovery UX; today tests
 * use it to pin the write side of `backupEnvelopeLocally`.
 */
export const readLocalVaultBackup = (
  envelopeId: string
): LocalVaultBackup | null => {
  try {
    const raw = localStorage.getItem(
      `${BACKUP_STORAGE_KEY_PREFIX}${envelopeId}`
    );
    if (raw === null) return null;
    return JSON.parse(raw) as LocalVaultBackup;
  } catch {
    return null;
  }
};

const verifyKeystoreChecksum = (data: KeystoreResponse): boolean => {
  const decodedBody = base64nopad.decode(data.body);
  const localChecksum = base64nopad.encode(sha256(decodedBody));
  return data.checksum === localChecksum;
};

/**
 * POST or PUT the envelope to the server, sending `If-Match` with the
 * previous sequence so the backend can reject conflicting writes
 * (backend issue B1 / app-backend#36). When `ifMatch` is null, the
 * request is a fresh create (POST).
 */
const postEnvelope = async (
  envelope: EnvelopeOuterSerialized,
  newSequence: number,
  ifMatch: number | null
): Promise<void> => {
  const envelopeJson = JSON.stringify(envelope);
  const body = base64nopad.encode(new TextEncoder().encode(envelopeJson));
  const checksum = base64nopad.encode(
    sha256(new TextEncoder().encode(envelopeJson))
  );
  const payload = JSON.stringify({ checksum, sequence: newSequence, body });

  const headers: Record<string, string> = {};
  if (ifMatch !== null) {
    headers['If-Match'] = String(ifMatch);
  }

  const response = await tdFetch(KEYSTORE_ENDPOINT, {
    method: ifMatch === null ? 'POST' : 'PUT',
    headers,
    body: payload,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to save vault: ${response.status} ${response.statusText}`
    );
  }
};

/**
 * Defer the localStorage probe — many test suites construct a
 * `VaultService` without ever calling `unlockVault`/`saveVault`, and
 * `localStorageTracker()` throws if storage is unavailable. Lazy
 * construction lets those suites instantiate even in headless contexts
 * where the API is not yet wired.
 */
const lazyLocalStorageTracker = (): RollbackTracker => {
  let inner: RollbackTracker | null = null;
  const ensure = (): RollbackTracker => {
    if (inner === null) inner = localStorageTracker();
    return inner;
  };
  return {
    getHighest: id => ensure().getHighest(id),
    setHighest: (id, c) => ensure().setHighest(id, c),
  };
};

/**
 * Document-level event names used by F16 (TdAccount fingerprint display)
 * and any future component that wants to react to unlock/lock without
 * holding a reference to `vaultService`.
 *
 *   `vault-unlocked` — fired after `createVault` / `unlockVault` succeeds.
 *   `vault-locked`   — fired after `lock()`.
 */
export const VAULT_UNLOCKED_EVENT = 'vault-unlocked';
export const VAULT_LOCKED_EVENT = 'vault-locked';

const dispatchVaultUnlocked = (): void => {
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new CustomEvent(VAULT_UNLOCKED_EVENT));
};

const dispatchVaultLocked = (): void => {
  if (typeof document === 'undefined') return;
  document.dispatchEvent(new CustomEvent(VAULT_LOCKED_EVENT));
};

/** Process-wide singleton consumed by main.js, components, etc. */
export const vaultService = new VaultService();

// #247: let `auth.logout()` warn before discarding unsaved vault state
// without importing this module (vault → tdfetch → auth import cycle).
registerUnsavedDataCheck(() => vaultService.hasUnsavedChanges());

/* -------------------------------------------------------------------------
 * Free-function exports for the legacy import surface.
 *
 * Storyprocessor.js (and other callers) reach for these names directly
 * rather than holding a `vaultService` reference. The free-function form
 * is just a thin proxy onto the process-wide singleton.
 *
 * #247 replaced the last deferred stubs (storeSymmetricKey /
 * retrieveSymmetricKey / listSymmetricKeys) with real implementations
 * backed by `VaultContents.symmetricKeys`.
 * ------------------------------------------------------------------------- */

export const signContent = (content: Uint8Array): SignedContent =>
  vaultService.signContent(content);

export const retrievePrivateKey = (
  identityId: string,
  algorithm: 'kem' | 'sig'
): Uint8Array | null => vaultService.retrievePrivateKey(identityId, algorithm);

export const storeSymmetricKey = (params: {
  id: string;
  key: Uint8Array;
}): void => vaultService.storeSymmetricKey(params);

export const retrieveSymmetricKey = (id: string): Uint8Array | null =>
  vaultService.retrieveSymmetricKey(id);

export const listSymmetricKeys = (): ReturnType<
  VaultService['listSymmetricKeys']
> => vaultService.listSymmetricKeys();

/**
 * Re-exports of error classes that consumers (UI, story flow) may want
 * to catch by type.
 */
export { EnvelopeOpenError, RollbackDetectedError };
